import { Link, usePage, router } from '@inertiajs/react'
import { type ReactNode } from 'react'

interface InvestorUser {
  id: number
  email: string
  last_signed_in_at: string | null
  investor_count: number
}

interface Props {
  children: ReactNode
}

export default function InvestorPortalLayout({ children }: Props) {
  const props = usePage().props
  const currentInvestor = (props as Record<string, unknown>).current_investor_user as InvestorUser | null
  const flash = (props as Record<string, unknown>).flash as { notice: string | null; alert: string | null }
  const path = usePage().url

  function isActive(href: string) {
    return path === href || path.startsWith(href + '?') || path.startsWith(href + '/')
  }

  function handleLogout() {
    router.delete('/investor-portal/logout')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link href="/investor-portal/dashboard" className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold text-sm">L</span>
                <span className="text-base font-semibold text-gray-900">Investor Portal</span>
              </Link>
              {currentInvestor && (
                <nav className="hidden sm:flex items-center gap-1">
                  <NavLink href="/investor-portal/dashboard" active={isActive('/investor-portal/dashboard')}>Dashboard</NavLink>
                  <NavLink href="/investor-portal/offerings" active={isActive('/investor-portal/offerings')}>Offerings</NavLink>
                  <NavLink href="/investor-portal/loans" active={isActive('/investor-portal/loans')}>My Loans</NavLink>
                  <NavLink href="/investor-portal/statements" active={isActive('/investor-portal/statements')}>Statements</NavLink>
                </nav>
              )}
            </div>
            {currentInvestor && (
              <div className="flex items-center gap-3">
                <span className="hidden sm:inline text-xs text-gray-500">{currentInvestor.email}</span>
                <button
                  onClick={handleLogout}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {flash.notice && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
            <p className="text-sm text-emerald-800">{flash.notice}</p>
          </div>
        </div>
      )}
      {flash.alert && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="rounded-lg bg-red-50 border border-red-200 p-3">
            <p className="text-sm text-red-800">{flash.alert}</p>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {children}
      </main>

      <footer className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8 mt-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400">
        <span>LendSolo Investor Portal</span>
        <Link href="/privacy" className="hover:text-gray-600">Privacy Policy</Link>
      </footer>
    </div>
  )
}

function NavLink({ href, active, children }: { href: string; active: boolean; children: ReactNode }) {
  return (
    <Link
      href={href}
      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
        active ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }`}
    >
      {children}
    </Link>
  )
}
